<template>
  <div class="container-fluid bg-white rounded-4 card shadow-sm comparacion-comision-simulador">
    <DxAccordion :collapsible="true" :multiple="false" :animation-duration="200" width="100%" class="comparacion-comision-simulador-accordion"
      item-title-template="tituloSimulador">
      <template #tituloSimulador>
        <h2 class="p-2 mb-0 fs-6">{{ $t("comparativa.detalle.simulador.titulo") }}</h2>
      </template>
      <DxItem>
        <div v-if="esLuz" class="d-flex gap-3 px-2 flex-lg-nowrap">
          <div class="d-flex flex-row gap-1 simulador-seccion-luz">
            <div class="d-flex flex-column">
              <label class="dx-field-item-label-text">
                <i class="fa-regular fa-xl fa-lightbulb text-success"></i>
                {{ $t("buscador-ofertas.simulador.potencia") }} (kW)
              </label>
              <div class="mt-2 d-flex gap-3 flex-wrap flex-xl-nowrap">
                <div v-for="periodo in periodosPotencia" :key="periodo.campo" class="d-flex flex-row align-items-center gap-1">
                  <label class="dx-field-item-label-text" :for="periodo.campo">{{ periodo.etiqueta }}</label>
                  <DxNumberBox :id="periodo.campo" v-model="model.DatosLuz[periodo.campo]" :min="potenciaMinLimit" :max="potenciaMaxLimit" />
                </div>
              </div>
            </div>
            <div class="d-flex flex-column border-start ps-3 ms-3">
              <label class="dx-field-item-label-text">{{ $t("buscador-ofertas.simulador.consumoEnergia") }} (kWh)</label>
              <div class="mt-2 d-flex gap-3 flex-wrap flex-xl-nowrap">
                <div v-for="periodo in periodosConsumo" :key="periodo.campo" class="d-flex flex-row align-items-center gap-1">
                  <label class="dx-field-item-label-text" :for="periodo.campo">{{ periodo.etiqueta }}</label>
                  <DxNumberBox :id="periodo.campo" v-model="model.DatosLuz[periodo.campo]" :min="consumoMinLimit" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="d-flex flex-column simulador-seccion-gas flex-shrink-0 px-2 pb-2">
          <label class="dx-field-item-label-text">
            <i class="fa-regular fa-xl fa-fire text-success"></i>
            {{ $t("buscador-ofertas.simulador.consumo") }} (kWh)
          </label>
          <div class="mt-2 d-flex">
            <DxNumberBox id="consumo-gas" v-model="model.DatosGas.Consumo" :min="consumoMinLimit" />
          </div>
        </div>
        <div class="d-flex justify-content-between align-items-center gap-3 pt-3 px-2">
          <p class="text-muted small mb-0 flex-shrink-1">
            {{ $t("comparativa.detalle.comision_simulacion_aviso") }}
          </p>
          <div class="flex-shrink-0">
            <DxButton v-bind="{ ...primaryButtonProps($t('comparativa.detalle.simulador.recalcular')), icon: 'fa-regular fa-calculator' }"
              @click="emitRecalcular" />
          </div>
        </div>
      </DxItem>
    </DxAccordion>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { DxButton, DxNumberBox } from "devextreme-vue";
import DxAccordion, { DxItem } from "devextreme-vue/accordion";
import { primaryButtonProps } from "@/.shared/helpers/button-helper";
import { SimuladorDataModel } from "@/components/buscador-ofertas-energia/models/SimuladorDataModel";
import { TipoSuministroTramiteEnergiaEnum, type ParametrosSimulacionComisionComparacionModel } from "@api/api";

const potenciaMinLimit = 0;
const potenciaMaxLimit = 1000;
const consumoMinLimit = 0;

const props = defineProps<{
  tipoSuministro?: TipoSuministroTramiteEnergiaEnum | null;
}>();

const model = reactive(new SimuladorDataModel());

const emit = defineEmits<{
  recalcular: [];
}>();

const esLuz = computed(() => props.tipoSuministro !== TipoSuministroTramiteEnergiaEnum.Gas);

type CampoPotenciaLuz = keyof Pick<SimuladorDataModel["DatosLuz"], "PotenciaP1" | "PotenciaP2" | "PotenciaP3" | "PotenciaP4" | "PotenciaP5" | "PotenciaP6">;
type CampoConsumoLuz = keyof Pick<SimuladorDataModel["DatosLuz"], "ConsumoP1" | "ConsumoP2" | "ConsumoP3" | "ConsumoP4" | "ConsumoP5" | "ConsumoP6">;

const periodosPotencia: { campo: CampoPotenciaLuz; etiqueta: string }[] = [
  { campo: "PotenciaP1", etiqueta: "P1" },
  { campo: "PotenciaP2", etiqueta: "P2" },
  { campo: "PotenciaP3", etiqueta: "P3" },
  { campo: "PotenciaP4", etiqueta: "P4" },
  { campo: "PotenciaP5", etiqueta: "P5" },
  { campo: "PotenciaP6", etiqueta: "P6" },
];

const periodosConsumo: { campo: CampoConsumoLuz; etiqueta: string }[] = [
  { campo: "ConsumoP1", etiqueta: "P1" },
  { campo: "ConsumoP2", etiqueta: "P2" },
  { campo: "ConsumoP3", etiqueta: "P3" },
  { campo: "ConsumoP4", etiqueta: "P4" },
  { campo: "ConsumoP5", etiqueta: "P5" },
  { campo: "ConsumoP6", etiqueta: "P6" },
];

function emitRecalcular(): void {
  emit("recalcular");
}

function establecerParametrosSimulacion(parametros?: ParametrosSimulacionComisionComparacionModel | null): void {
  if (!parametros) {
    return;
  }

  if (parametros.DatosLuz) {
    model.DatosLuz.PotenciaP1 = parametros.DatosLuz.PotenciaP1 ?? null;
    model.DatosLuz.PotenciaP2 = parametros.DatosLuz.PotenciaP2 ?? null;
    model.DatosLuz.PotenciaP3 = parametros.DatosLuz.PotenciaP3 ?? null;
    model.DatosLuz.PotenciaP4 = parametros.DatosLuz.PotenciaP4 ?? null;
    model.DatosLuz.PotenciaP5 = parametros.DatosLuz.PotenciaP5 ?? null;
    model.DatosLuz.PotenciaP6 = parametros.DatosLuz.PotenciaP6 ?? null;
    model.DatosLuz.ConsumoP1 = parametros.DatosLuz.ConsumoP1 ?? null;
    model.DatosLuz.ConsumoP2 = parametros.DatosLuz.ConsumoP2 ?? null;
    model.DatosLuz.ConsumoP3 = parametros.DatosLuz.ConsumoP3 ?? null;
    model.DatosLuz.ConsumoP4 = parametros.DatosLuz.ConsumoP4 ?? null;
    model.DatosLuz.ConsumoP5 = parametros.DatosLuz.ConsumoP5 ?? null;
    model.DatosLuz.ConsumoP6 = parametros.DatosLuz.ConsumoP6 ?? null;
  }

  if (parametros.DatosGas) {
    model.DatosGas.Consumo = parametros.DatosGas.Consumo ?? null;
  }
}

function obtenerParametrosSimulacion(): ParametrosSimulacionComisionComparacionModel {
  if (esLuz.value) {
    return {
      DatosLuz: { ...model.DatosLuz },
      DatosGas: null,
    };
  }

  return {
    DatosGas: { ...model.DatosGas },
    DatosLuz: null,
  };
}

defineExpose({
  establecerParametrosSimulacion,
  obtenerParametrosSimulacion,
});
</script>

<style lang="scss" scoped>
.comparacion-comision-simulador {
  margin-bottom: var(--spacing-sm);
}

:deep(.comparacion-comision-simulador-accordion .dx-accordion-item > .dx-accordion-item-title) {
  background: var(--background-surface-default) !important;
  color: var(--text-primary-default) !important;
}
:deep(.comparacion-comision-simulador-accordion.dx-accordion) {
  border: none;
  background: transparent;
}

:deep(.comparacion-comision-simulador-accordion .dx-accordion-item-title-caption) {
  order: 1;
  margin-inline-end: 0;
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.comparacion-comision-simulador-accordion .dx-accordion-item-title::before) {
  order: 2;
  margin-inline-end: 0;
  margin-inline-start: auto;
}

.simulador-seccion-luz,
.simulador-seccion-gas {
  border-left: 4px solid var(--border-success-default);
  padding-left: 1rem;
  box-sizing: border-box;
}
</style>
